import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import api from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Printer, Package, Buildings, Drop, Truck } from "@phosphor-icons/react";

const today = new Date().toISOString().slice(0, 10);

export default function DailyReportPage() {
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/daily?date=${d}`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };

  useAutoRefresh(() => load(date), { deps: [date], interval: 0 });

  const print = () => {
    if (!data) return;
    const s = data.summary;
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) return;
    const fmt = (n) => (n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
    const tRows = (arr, cols) => arr.map((r) => `<tr>${cols.map((c) => `<td>${c(r)}</td>`).join("")}</tr>`).join("");

    w.document.write(`<!DOCTYPE html><html><head><title>Laporan Harian ${data.date}</title>
      <style>
        body{font-family:sans-serif;padding:32px;color:#0f172a;font-size:12px}
        h1{font-size:22px;margin:0 0 6px}
        h2{font-size:14px;margin:24px 0 8px;border-bottom:2px solid #0ea5e9;padding-bottom:4px;text-transform:uppercase;letter-spacing:0.05em}
        .header{display:flex;align-items:center;gap:12px;border-bottom:2px solid #0f172a;padding-bottom:12px}
        img{width:56px;height:56px;border-radius:50%}
        .cards{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin:16px 0}
        .card{border:1px solid #e2e8f0;padding:10px;border-radius:6px;background:#f8fafc}
        .card .lbl{font-size:9px;text-transform:uppercase;letter-spacing:0.08em;color:#64748b}
        .card .val{font-size:22px;font-weight:900;color:#0f172a;margin-top:2px}
        table{width:100%;border-collapse:collapse;font-size:11px;margin-top:4px}
        th{text-align:left;padding:6px;background:#f1f5f9;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#475569}
        td{padding:6px;border-bottom:1px solid #f1f5f9}
        .mono{font-family:monospace}
        .empty{color:#94a3b8;font-style:italic;padding:12px;text-align:center}
        @media print{body{padding:16px}}
      </style></head><body>
      <div class="header">
        <img src="/primergy-logo.jpeg" />
        <div>
          <div style="font-weight:900;font-size:16px">PT. PRIMERGY SOLUTION</div>
          <div style="font-size:11px;color:#64748b">Laporan Harian Operasional Pabrik</div>
        </div>
        <div style="margin-left:auto;text-align:right">
          <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:#64748b">Tanggal Laporan</div>
          <div style="font-weight:800;font-size:16px">${new Date(data.date).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
        </div>
      </div>

      <h2>Ringkasan Hari Ini</h2>
      <div class="cards">
        <div class="card"><div class="lbl">Receiving</div><div class="val">${s.receiving_count}</div></div>
        <div class="card"><div class="lbl">Delivery</div><div class="val">${s.delivery_count}</div></div>
        <div class="card"><div class="lbl">Opname</div><div class="val">${s.opname_count}</div></div>
        <div class="card"><div class="lbl">Gate Movement</div><div class="val">${s.gate_movement}</div></div>
        <div class="card"><div class="lbl">Oli Bekas Diterima (L)</div><div class="val">${fmt(s.oli_bekas_received_liter)}</div></div>
        <div class="card"><div class="lbl">Presol Produced (L)</div><div class="val">${fmt(s.presol_produced_liter)}</div></div>
        <div class="card"><div class="lbl">Minyak Bakar (L)</div><div class="val">${fmt(s.minyak_bakar_produced_liter)}</div></div>
        <div class="card"><div class="lbl">Approval Diproses</div><div class="val">${s.approvals_processed}</div></div>
      </div>

      <h2>Receiving Hari Ini</h2>
      ${data.receivings.length === 0 ? '<div class="empty">Tidak ada receiving.</div>' :
        `<table><thead><tr><th>No. Ref</th><th>Supplier</th><th>Barang</th><th>Tank</th><th>Kg Timbang</th><th>Yield %</th><th>Presol (L)</th><th>Minyak Bakar (L)</th></tr></thead><tbody>${
          tRows(data.receivings, [
            (r)=>`<span class="mono">${r.ref_no}</span>`, (r)=>r.supplier, (r)=>r.material, (r)=>r.destination_tank||"-",
            (r)=>`<span class="mono">${fmt(r.gross_in||0)}</span>`, (r)=>r.lab_result?.yield_rate ?? "-",
            (r)=>`<span class="mono">${fmt(r.lab_result?.presol_qty||0)}</span>`,
            (r)=>`<span class="mono">${fmt(r.lab_result?.minyak_bakar_qty||0)}</span>`,
          ])}</tbody></table>`}

      <h2>Delivery Hari Ini</h2>
      ${data.deliveries.length === 0 ? '<div class="empty">Tidak ada delivery.</div>' :
        `<table><thead><tr><th>No. DO</th><th>Customer</th><th>Produk</th><th>Target</th><th>Truck</th><th>Status</th></tr></thead><tbody>${
          tRows(data.deliveries, [
            (r)=>`<span class="mono">${r.do_no}</span>`, (r)=>r.customer, (r)=>r.product,
            (r)=>`<span class="mono">${fmt(r.target_qty)}</span>`, (r)=>`<span class="mono">${r.truck_no}</span>`, (r)=>r.status,
          ])}</tbody></table>`}

      <h2>Stok Opname</h2>
      ${data.opnames.length === 0 ? '<div class="empty">Tidak ada opname.</div>' :
        `<table><thead><tr><th>Tank/Point</th><th>Produk</th><th>Ukur</th><th>Buku</th><th>Selisih</th><th>%</th><th>Status</th></tr></thead><tbody>${
          tRows(data.opnames, [
            (r)=>r.tank_name, (r)=>r.product, (r)=>`<span class="mono">${fmt(r.measured_qty)}</span>`,
            (r)=>`<span class="mono">${fmt(r.book_stock)}</span>`,
            (r)=>`<span class="mono">${r.variance > 0 ? "+" : ""}${fmt(r.variance)}</span>`,
            (r)=>`${(r.variance_pct||0).toFixed(2)}%`, (r)=>r.status,
          ])}</tbody></table>`}

      <h2>Riwayat Approval</h2>
      ${data.approvals.length === 0 ? '<div class="empty">Tidak ada approval hari ini.</div>' :
        `<table><thead><tr><th>DO</th><th>Customer</th><th>Keputusan</th><th>Oleh</th><th>Alasan Tolak</th></tr></thead><tbody>${
          tRows(data.approvals, [
            (r)=>`<span class="mono">${r.do_no}</span>`, (r)=>r.customer,
            (r)=>r.approval_status === "approved" ? "✓ Disetujui" : "✗ Ditolak",
            (r)=>r.approved_by_name || "-",
            (r)=>r.reject_reason || "-",
          ])}</tbody></table>`}

      <div style="margin-top:40px;font-size:10px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:8px">
        Dicetak: ${new Date().toLocaleString("id-ID")} · PT. Primergy Solution — Sistem Manajemen Pabrik
      </div>
      <script>setTimeout(()=>window.print(),300)</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <AppShell
      title="Laporan Harian"
      subtitle="Ringkasan operasional pabrik per tanggal — bisa diprint / disimpan PDF"
      actions={
        <Button onClick={print} disabled={!data} data-testid="report-print" className="bg-emerald-600 hover:bg-emerald-700 h-10 gap-2">
          <Printer size={16} weight="bold" /> <span className="hidden sm:inline">Print / PDF</span>
        </Button>
      }
    >
      <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-4 mb-6 flex flex-wrap items-center gap-3">
        <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">Tanggal:</label>
        <Input data-testid="report-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="max-w-xs h-10" />
        <Button onClick={() => load(date)} data-testid="report-load" disabled={loading} className="bg-sky-600 hover:bg-sky-700 h-10">
          {loading ? "Memuat…" : "Tampilkan"}
        </Button>
      </div>

      {data ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card icon={Package} tone="sky" label="Receiving" value={data.summary.receiving_count} />
            <Card icon={Buildings} tone="sky" label="Delivery" value={data.summary.delivery_count} />
            <Card icon={Drop} tone="emerald" label="Opname" value={data.summary.opname_count} />
            <Card icon={Truck} tone="navy" label="Gate Movement" value={data.summary.gate_movement} />
            <Card icon={Package} tone="amber" label="Oli Diterima (L)" value={data.summary.oli_bekas_received_liter} />
            <Card icon={Drop} tone="sky" label="Presol Produced (L)" value={data.summary.presol_produced_liter} />
            <Card icon={Drop} tone="red" label="Minyak Bakar (L)" value={data.summary.minyak_bakar_produced_liter} />
            <Card icon={Buildings} tone="emerald" label="Approval Diproses" value={data.summary.approvals_processed} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title={`Receiving (${data.receivings.length})`} rows={data.receivings.map((r) => ({
              k: r.ref_no, l: `${r.supplier} · ${r.material}`, r: r.lab_result ? `${(r.lab_result.presol_qty||0).toLocaleString("id-ID")} L Presol` : "Lab pending",
            }))} />
            <Section title={`Delivery (${data.deliveries.length})`} rows={data.deliveries.map((r) => ({
              k: r.do_no, l: `${r.customer} · ${r.product}`, r: `${(r.target_qty||0).toLocaleString("id-ID")} L`,
            }))} />
            <Section title={`Opname (${data.opnames.length})`} rows={data.opnames.map((r) => ({
              k: r.tank_name, l: r.product, r: `${(r.variance||0).toLocaleString("id-ID")} (${(r.variance_pct||0).toFixed(2)}%)`,
            }))} />
            <Section title={`Approval (${data.approvals.length})`} rows={data.approvals.map((r) => ({
              k: r.do_no, l: `${r.customer} · oleh ${r.approved_by_name}`, r: r.approval_status === "approved" ? "✓ Setuju" : "✗ Tolak",
            }))} />
          </div>
        </>
      ) : (
        <div className="text-center py-16 text-slate-400 text-sm">Pilih tanggal dan klik <b>Tampilkan</b>.</div>
      )}
    </AppShell>
  );
}

const TONE = {
  navy:"bg-slate-900 text-white", sky:"bg-sky-50 text-sky-900 border-sky-100",
  amber:"bg-amber-50 text-amber-900 border-amber-100", emerald:"bg-emerald-50 text-emerald-900 border-emerald-100",
  red:"bg-red-50 text-red-900 border-red-100",
};

function Card({ icon: Icon, tone, label, value }) {
  return (
    <div className={`rounded-xl p-4 border ${TONE[tone]} shadow-sm`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] opacity-70">{label}</div>
        <Icon size={18} weight="duotone" className="opacity-80" />
      </div>
      <div className="font-heading font-black text-3xl mt-2 leading-none">
        {(value || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}

function Section({ title, rows }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">{title}</h3>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
        {rows.length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-xs">Tidak ada data.</div>
        ) : rows.map((r, i) => (
          <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[13px] font-bold text-slate-900 truncate">{r.k}</div>
              <div className="text-[11px] text-slate-500 truncate">{r.l}</div>
            </div>
            <div className="text-[12px] font-mono font-semibold text-slate-700 shrink-0">{r.r}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
